import * as Checkbox from '@radix-ui/react-checkbox';
import * as Dialog from '@radix-ui/react-dialog';
import { useEffect, useRef, useState, type ReactElement } from 'react';

import type { DeclarationTemplate } from '../lib/registration-api';

function scrollReachedEnd(element: HTMLElement | null): boolean {
  if (!element) return false;
  return element.scrollHeight <= element.clientHeight + 1
    || element.scrollTop + element.clientHeight >= element.scrollHeight - 8;
}

function declarationText(template: DeclarationTemplate): string {
  const parts = [template.bodyText];
  if (template.examFeeNoteText) {
    parts.push('', 'Cambridge examination fees', template.examFeeNoteText);
  }
  return parts.join('\n');
}

export function DeclarationModal(props: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  template: DeclarationTemplate | undefined;
  accepted: boolean;
  onAccept: () => void;
}): ReactElement {
  const { open, onOpenChange, template, accepted, onAccept } = props;
  const scrollRef = useRef<HTMLDivElement>(null);
  const [scrolledToEnd, setScrolledToEnd] = useState(false);
  const [checked, setChecked] = useState(false);

  useEffect(() => {
    if (!open) {
      setChecked(false);
      setScrolledToEnd(false);
      return;
    }
    const frame = requestAnimationFrame(() => {
      setScrolledToEnd(scrollReachedEnd(scrollRef.current));
    });
    return () => cancelAnimationFrame(frame);
  }, [open, template]);

  const fullText = template ? declarationText(template) : '';

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="modal-overlay" />
        <Dialog.Content className="modal-content" aria-describedby="declaration-body">
          <Dialog.Title className="modal-title">Declaration and parent/legal guardian consent</Dialog.Title>
          <Dialog.Description className="modal-description">
            Read the full declaration, including the Cambridge examination-fee notice, then accept to continue to payment.
          </Dialog.Description>
          <div
            id="declaration-body"
            ref={scrollRef}
            className="declaration-scroll modal-declaration-scroll"
            onScroll={() => setScrolledToEnd(scrollReachedEnd(scrollRef.current))}
          >
            {fullText.split('\n').map((paragraph, index) => (
              <p key={`${index}-${paragraph.slice(0, 12)}`}>{paragraph || '\u00a0'}</p>
            ))}
          </div>
          {template ? (
            <p className="dev-hint">
              Declaration v{template.version}, terms {template.termsVersion}
              {template.examFeeNoteVersion ? `, exam-fee note v${template.examFeeNoteVersion}` : ''}
            </p>
          ) : null}
          <label className="checkbox-row">
            <Checkbox.Root
              className="checkbox-root"
              checked={checked}
              disabled={!scrolledToEnd}
              onCheckedChange={(value) => setChecked(value === true)}
            >
              <Checkbox.Indicator>✓</Checkbox.Indicator>
            </Checkbox.Root>
            <span>I have read and accept the declaration above.</span>
          </label>
          <div className="modal-actions">
            <button className="btn btn-secondary" type="button" onClick={() => onOpenChange(false)}>
              Cancel
            </button>
            <button
              className="btn"
              type="button"
              disabled={!checked}
              onClick={() => {
                onAccept();
                onOpenChange(false);
              }}
            >
              {accepted ? 'Accept again' : 'Accept declaration'}
            </button>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
